import { useState } from 'react';
import { X } from 'lucide-react';
import { TeamMember } from '../../lib/types';

interface EditMemberModalProps {
  member: TeamMember;
  onClose: () => void;
  onSave: (member: TeamMember, file: File | null) => Promise<void>;
}

export function EditMemberModal({ member, onClose, onSave }: EditMemberModalProps) {
  const [editedMember, setEditedMember] = useState({
    ...member,
    specialties: member.specialties.join(', '),
    achievements: member.achievements.join('\n'),
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(
      {
        ...member,
        ...editedMember,
        specialties: editedMember.specialties.split(',').map(s => s.trim()),
        achievements: editedMember.achievements.split('\n').filter(a => a.trim()),
      },
      selectedFile
    );
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Edit Team Member</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={editedMember.name}
              onChange={(e) => setEditedMember({ ...editedMember, name: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              value={editedMember.title}
              onChange={(e) => setEditedMember({ ...editedMember, title: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={editedMember.description}
              onChange={(e) => setEditedMember({ ...editedMember, description: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Specialties (comma-separated)</label>
            <input
              type="text"
              value={editedMember.specialties}
              onChange={(e) => setEditedMember({ ...editedMember, specialties: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Achievements (one per line)</label>
            <textarea
              value={editedMember.achievements}
              onChange={(e) => setEditedMember({ ...editedMember, achievements: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Profile Image</label>
            <div className="mt-1 flex items-center gap-4">
              <img src={member.imageUrl} alt={member.name} className="w-20 h-20 object-cover rounded-lg" />
              <input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="block w-full"
                accept="image/*"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}